import { useCreateReport, useSearchMentors } from '@actions';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import { useDebounce } from '@hooks';
import { reportApis } from '@shared/apis';
import useToast from 'apps/agora/src/hooks/useToast';
import moment from 'moment';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

type GenerateReportModalProps = {
  isOpen: boolean;
  selectedDate: string;
  onClose: () => void;
};

const GenerateReportModal = (props: GenerateReportModalProps) => {
  const { isOpen, selectedDate, onClose } = props;

  const toast = useToast();
  const queryClient = useQueryClient();

  const [mentorId, setMentorId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const monthLabel = moment(selectedDate, 'YYYY-MM').format('MMMM YYYY');
  const reportDate = moment(selectedDate, 'YYYY-MM').add(1, 'month').date(2).format('YYYY-MM-DD');

  const { data: mentorsData, isLoading: isLoadingMentors } = useSearchMentors({
    page: 1,
    limit: 20,
    sorting: {},
    search: debouncedSearch,
  });

  const mentorOptions = (mentorsData?.mentors ?? []).map((entry) => ({
    label: entry.mentor.fullName,
    value: entry.mentor._id,
  }));

  const { mutate: createReport, isLoading } = useCreateReport(mentorId ?? '', {
    onSuccess: () => {
      queryClient.invalidateQueries(reportApis.searchReports.endpoint);
      toast.success(`Report generated for ${monthLabel}. The PDF is queued and will be sent soon.`);
      setMentorId(undefined);
      setSearch('');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'The report could not be generated.');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      <Modal.Header title={`Generate a report for ${monthLabel}`} className="text-center" />
      <Modal.Body className="flex flex-col gap-4 overflow-x-hidden mb-6 text-xs leading-5">
        <p>
          Use this when a mentor is missing from the month entirely, for example because the monthly
          job failed. If the mentor already has a report, regenerate it from its row instead.
        </p>

        <Select
          label="Mentor"
          placeholder="Search a mentor by name"
          showSearch
          isLoading={isLoadingMentors}
          value={mentorId}
          options={mentorOptions}
          onSearch={(value) => setSearch(value)}
          onSelect={(value) => setMentorId(value as string)}
          filterOption={false}
        />

        <p className="text-customGrey">
          Sessions held up to the end of {monthLabel} that have not been paid yet will be included.
        </p>
      </Modal.Body>
      <Modal.Footer className="flex justify-center gap-4">
        <Button
          className="h-6 min-h-6"
          size="large"
          variant="secondary"
          buttonText="Cancel"
          onClick={onClose}
        />
        <Button
          className="h-6 min-h-6"
          size="large"
          variant="primary"
          buttonText="Generate report"
          isLoading={isLoading}
          disabled={!mentorId}
          onClick={() => createReport({ date: reportDate })}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateReportModal;
